import React from 'react';
import camelCase from 'lodash/camelCase';
import { useWindowSize } from 'react-use';

import { Card, CardActionArea, Typography, Box } from '@mui/material';

import { useTranslation } from 'next-i18next';

import ZoomableImage from 'src/components/ui/ZoomableImage';

import useAppRouter from 'src/hooks/useAppRouter';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';

import type { Activity, ActivityCategory } from 'src/types/__generated__/graphql';
import { routePathCommunityCategory, routePathCommunitySearch } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';

interface StyledCardInterface {
    $greyBackground?: boolean;
}

const StyledCard = styledComponent(Card)`
    &.MuiCard-root {
        border: 0.0625rem solid ${colours.lightBorderColor};
        box-sizing: border-box;
        box-shadow: 0 0.25rem 0.625rem ${colours.greyOpacity.O10};
    }
    ${(props: StyledCardInterface) => props.$greyBackground && ` background-color: ${colours.grey.G100};`}
`;

type Props = {
    activity: Activity | ActivityCategory;
    activityCategory?: boolean;
    greyBackground?: boolean;
};

const ActivityCard: React.FC<Props> = ({ activity, activityCategory, greyBackground }) => {
    const { t } = useTranslation(['common']);
    const router = useAppRouter();
    const { width } = useWindowSize();
    const widthBetweenMobileAndTablet = width > breakpoints.mobile && width < breakpoints.tabletPortrait;

    return (
        <StyledCard $greyBackground={greyBackground} elevation={0}>
            <CardActionArea
                component="a"
                style={{ display: 'inline-block' }}
                data-target-id="activity-card-link"
                href={
                    activityCategory
                        ? `${routePathCommunityCategory}/${activity.id}`
                        : `${routePathCommunitySearch}?activityId=${activity.id}&activityType=user`
                }
                onClick={async (ev: React.MouseEvent) => {
                    ev.preventDefault();
                    await router.push(String(ev.currentTarget.getAttribute('href')));
                }}
            >
                <Box overflow="hidden">
                    <Box display="grid" justifyContent="center" alignItems="center">
                        <ZoomableImage
                            src={`${process.env.NEXT_PUBLIC_SITE_URL}/assets/images/activity/${activity.labelKey}.jpg`}
                            alt={String(activity.labelKey)}
                            height={136}
                            width={widthBetweenMobileAndTablet ? 750 : 350}
                            style={{ objectFit: 'cover', minWidth: '100%', minHeight: '100%' }}
                        />
                    </Box>
                    <Box height="3.75rem" mx="1.25rem" display="flex" flexDirection="column" justifyContent="center">
                        <Typography data-target-id="activity-label-key" variant="h5">
                            {t(`common:activities.${camelCase(activity.labelKey)}`)}
                        </Typography>
                        {activityCategory && (
                            <Box style={{ opacity: 0.6 }}>
                                <Typography data-target-id="activity-subtitle" variant="caption">
                                    {`${(activity as ActivityCategory).activities?.length} ${t(
                                        'community:professionnalActivities',
                                    )}`}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>
            </CardActionArea>
        </StyledCard>
    );
};

export default ActivityCard;
